import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Activity, Heart, Trophy, MapPin, Shield, Sparkles, Flame } from 'lucide-react';
import { ScoreLogo } from '../components/Logo';

export const LandingPage = () => {
  const navigate = useNavigate();

// Pyramid/Levels Icon for Skill Levels
const PyramidIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Bottom level - widest */}
    <rect x="2" y="18" width="20" height="4" rx="1" fill="currentColor" opacity="0.3" />
    {/* Middle level */}
    <rect x="5" y="12" width="14" height="4" rx="1" fill="currentColor" opacity="0.6" />
    {/* Top level - narrowest */}
    <rect x="8" y="6" width="8" height="4" rx="1" fill="currentColor" />
    {/* Peak/star */}
    <path d="M12 2 L13 5 L12 4 L11 5 Z" fill="currentColor" />
  </svg>
);

  const features = [
    {
      icon: <Activity className="w-8 h-8" />,
      title: "SPORTS-FOCUSED",
      description: "Connect with singles who share your passion for staying active"
    },
    {
      icon: <PyramidIcon className="w-8 h-8" />,
      title: "SKILL LEVELS",
      description: "Find matches at your performance level - from beginner to pro"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "LOCAL ATHLETES",
      description: "Discover active singles in your area ready to play and date"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "VERIFIED PROFILES",
      description: "Selfie verification ensures you match with real people"
    }
  ];

  const mockProfiles = [
    {
      name: "Alex, 28",
      sport: "CrossFit & Lifting",
      image: "https://images.unsplash.com/photo-1633354129320-163fb10dd288?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      level: "advanced"
    },
    {
      name: "Marcus, 31",
      sport: "Hiking & Alpinism",
      image: "https://images.unsplash.com/photo-1705466877249-ce2c0d2e1baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      level: "elite"
    },
    {
      name: "Sarah, 26",
      sport: "Marathon Runner",
      image: "https://images.pexels.com/photos/4853112/pexels-photo-4853112.jpeg?w=400",
      level: "college_pro"
    }
  ];

  const getLevelColor = (level) => {
    const colors = {
      beginner: 'bg-green-500',
      intermediate: 'bg-blue-500',
      advanced: 'bg-yellow-500',
      elite: 'bg-orange-500',
      college_pro: 'bg-red-500'
    };
    return colors[level] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image - Man and woman playing pickleball */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/fa07e0ac-a83e-4040-9ba7-85fdea1e37d7/images/188388153ae9d96cf99d12449dcba8e66342b733aad43da98e88040b0f1a9c54.png')`,
          }}
        >
          {/* Darker overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 p-8 md:p-12">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ScoreLogo size={44} variant="light" />
              <span className="font-display font-black text-2xl tracking-tight text-white">SCORE</span>
            </div>
            <Button 
              data-testid="nav-login-btn"
              variant="ghost" 
              className="text-white/80 hover:text-white hover:bg-white/10 font-body px-6 py-2 rounded-full transition-colors"
              onClick={() => navigate('/auth')}
            >
              Log In
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-12 lg:px-24 py-40">
          <div className="max-w-2xl">
            <h1 className="font-display font-black text-6xl md:text-8xl tracking-wide uppercase leading-[0.95] mb-8 text-white" style={{ letterSpacing: '0.05em' }}>
              FIND YOUR
              <span className="block text-primary">PERFECT</span>
              MATCH
            </h1>
            <p className="font-body text-lg md:text-xl text-white/80 mb-12 max-w-lg leading-relaxed">
              The dating app for athletes and active singles. Connect with people who share your passion for sports and fitness.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <Button 
                data-testid="get-started-btn"
                size="lg" 
                className="bg-primary hover:bg-red-600 text-white font-display font-bold text-lg uppercase tracking-wide px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                onClick={() => navigate('/auth')}
              >
                Get Started
              </Button>
              <Button 
                data-testid="learn-more-btn"
                size="lg" 
                variant="outline"
                className="border border-white/30 hover:border-white/50 text-white font-display font-bold text-lg uppercase tracking-wide px-10 py-6 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-8 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tight uppercase mb-6 text-gray-900">
              WHY <span className="text-primary">SCORE</span>?
            </h2>
            <p className="font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Designed for athletes and fitness enthusiasts who want more than just a swipe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-10 rounded-3xl bg-white border border-gray-100 hover:border-primary/30 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-8 group-hover:bg-primary group-hover:text-white transition-colors duration-500">
                  {feature.icon}
                </div>
                <h3 className="font-display font-bold text-xl uppercase mb-4 tracking-tight text-gray-900">{feature.title}</h3>
                <p className="font-body text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Preview Section */}
      <section className="py-32 px-8 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tight uppercase mb-6 text-gray-900">
              MEET <span className="text-primary">ATHLETES</span>
            </h2>
            <p className="font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Real profiles from active singles in your area
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {mockProfiles.map((profile, index) => (
              <div 
                key={index}
                className="relative rounded-3xl overflow-hidden aspect-[3/4] group cursor-pointer shadow-lg hover:shadow-2xl transition-shadow duration-300"
              >
                <img 
                  src={profile.image} 
                  alt={profile.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-2 h-2 rounded-full ${getLevelColor(profile.level)}`} />
                    <span className="font-body text-sm text-white/80">{profile.sport}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl tracking-tight text-white">{profile.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-40 px-8 md:px-12 lg:px-24 bg-white relative">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-10">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-body text-sm text-primary">AI-Powered Matching</span>
          </div>
          <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter uppercase mb-8 leading-[0.9] text-gray-900">
            READY TO<br />
            <span className="text-primary">SCORE</span>?
          </h2>
          <p className="font-body text-lg text-gray-500 mb-12 max-w-xl mx-auto leading-relaxed">
            Join thousands of athletes finding meaningful connections through shared passion for sports.
          </p>
          <Button 
            data-testid="cta-join-btn"
            size="lg" 
            className="bg-primary hover:bg-red-600 text-white font-display font-bold text-xl uppercase tracking-wide px-14 py-7 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            onClick={() => navigate('/auth')}
          >
            Join Score Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 md:px-12 lg:px-24 border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <ScoreLogo size={36} />
            <span className="font-display font-bold text-lg tracking-tight text-gray-900">SCORE</span>
          </div>
          <p className="font-body text-sm text-gray-500">
            © 2025 Score Dating. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
